
import { defineComponent, PropType, ref, watch, computed } from 'vue'
import { DashboardFilters, FilterOptions } from '../../types'
import BaseInputOutlined from '@/components/inputs/BaseInputOutlined.vue'
import CustomActivatorSelect from '@/components/inputs/CustomActivatorSelect.vue'

export default defineComponent({
    name: 'Filters',
    components: {
        BaseInputOutlined,
        CustomActivatorSelect,
    },
    props: {
        filters: {
            type: Object as PropType<DashboardFilters>,
            required: true,
        },
        filterOptions: {
            type: Object as PropType<FilterOptions>,
            required: true,
        },
        activePlanId: {
            type: String,
            default: '',
        },
        activePlanIds: {
            type: Array as () => string[],
            default: () => [],
        },
        canCreateShifts: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['filters-change', 'add-shift'],
    setup(props, { emit }) {
        // Локальная копия фильтров для реактивности
        const localFilters = ref<DashboardFilters>({ ...props.filters } as any)

        // Отображаемые значения для полей ввода
        const planDisplayValue = ref('')
        const monthDisplayValue = ref('')
        const yearDisplayValue = ref('')
        const administratorDisplayValue = ref('')
        const branchDisplayValue = ref('')

        // Опции для месяцев и лет
        const monthOptions = [
            { value: 1, label: 'Январь' },
            { value: 2, label: 'Февраль' },
            { value: 3, label: 'Март' },
            { value: 4, label: 'Апрель' },
            { value: 5, label: 'Май' },
            { value: 6, label: 'Июнь' },
            { value: 7, label: 'Июль' },
            { value: 8, label: 'Август' },
            { value: 9, label: 'Сентябрь' },
            { value: 10, label: 'Октябрь' },
            { value: 11, label: 'Ноябрь' },
            { value: 12, label: 'Декабрь' },
        ]

        const yearOptions = [
            { value: 2024, label: '2024' },
            { value: 2025, label: '2025' },
            { value: 2026, label: '2026' },
        ]

        // Опции для администраторов с "Все"
        const administratorOptions = computed(() => [
            { id: '', name: 'Все' },
            ...(props.filterOptions.administrators || []),
        ])

        // Опции для салонов с "Все"
        const branchOptions = computed(() => [
            { id: '', name: 'Все' },
            ...(props.filterOptions.branches || []),
        ])

        // Опции для каналов с "Все"

        // Проверяем, является ли выбранный период текущим месяцем
        const isCurrentMonth = computed(() => {
            const currentDate = new Date()
            const currentMonth = currentDate.getMonth() + 1
            const currentYear = currentDate.getFullYear()

            return (
                localFilters.value.period.month === currentMonth &&
                localFilters.value.period.year === currentYear
            )
        })

        // Проверяем готовность данных фильтров
        const hasBranches = computed(() => {
            return props.filterOptions.branches && props.filterOptions.branches.length > 0
        })


        const hasAdministrators = computed(() => {
            return props.filterOptions.administrators && props.filterOptions.administrators.length > 0
        })

        // Проверяем, выбран ли активный план
        const isActivePlanSelected = computed(() => {
            const selectedPlanId = localFilters.value.plan
            if (!selectedPlanId) return false
            return props.activePlanIds.includes(selectedPlanId)
        })

        const selectedPlan = computed(() => {
            return props.filterOptions.plans?.find(p => p.id === localFilters.value.plan) || null
        })

        const selectedPlanIsActive = computed(() => {
            return Boolean(selectedPlan.value?.isActive)
        })

        // Обновляем отображаемые значения
        const updateDisplayValues = () => {
            // План
            planDisplayValue.value = selectedPlan.value?.name || 'Выберите план'

            // Месяц
            const selectedMonth = monthOptions.find(m => m.value === localFilters.value.period.month)
            monthDisplayValue.value = selectedMonth?.label || 'Месяц'

            // Год
            const selectedYear = yearOptions.find(y => y.value === localFilters.value.period.year)
            yearDisplayValue.value = selectedYear?.label || 'Год'

            // Администратор
            const selectedAdmin = administratorOptions.value.find(
                a => a.id === localFilters.value.administrator
            )
            administratorDisplayValue.value = selectedAdmin?.name || 'Выберите администратора'

            // Салон
            const selectedBranch = branchOptions.value.find(b => b.id === localFilters.value.branch)
            branchDisplayValue.value = selectedBranch?.name || 'Выберите салон'

        }

        // Отслеживаем изменения в пропсах
        watch(
            () => props.filters,
            newFilters => {
                localFilters.value = { ...newFilters } as any
                updateDisplayValues()
            },
            { deep: true }
        )

        // Отслеживаем изменения в filterOptions для обновления отображаемых значений
        watch(
            () => props.filterOptions,
            () => {
                updateDisplayValues()
            },
            { deep: true }
        )

        // Инициализируем отображаемые значения
        updateDisplayValues()

        const onPlanChange = (planId: string) => {
            localFilters.value.plan = planId
            localFilters.value.branch = ''
            emit('filters-change', { ...localFilters.value })
        }

        const onMonthChange = (month: number) => {
            localFilters.value.period.month = month
            emit('filters-change', { ...localFilters.value })
        }

        const onYearChange = (year: number) => {
            localFilters.value.period.year = year
            emit('filters-change', { ...localFilters.value })
        }

        const onAdministratorChange = (adminId: string) => {
            localFilters.value.administrator = adminId
            emit('filters-change', { ...localFilters.value })
        }

        const onBranchChange = (branchId: string) => {
            localFilters.value.branch = branchId
            emit('filters-change', { ...localFilters.value })
        }


        const onAddShift = () => {
            emit('add-shift')
        }

        return {
            localFilters,
            planDisplayValue,
            monthDisplayValue,
            yearDisplayValue,
            administratorDisplayValue,
            branchDisplayValue,
            monthOptions,
            yearOptions,
            administratorOptions,
            branchOptions,
            isCurrentMonth,
            hasBranches,
            hasAdministrators,
            isActivePlanSelected,
            selectedPlanIsActive,
            onPlanChange,
            onMonthChange,
            onYearChange,
            onAdministratorChange,
            onBranchChange,
            onAddShift,
        }
    },
})
